"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Loading = exports.LoadingButton = exports.LoadingOverlay = exports.LoadingDots = exports.LoadingSpinner = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * RedPanda UI - Loading Component
 * 統一的載入元件，支援多種樣式和大小
 */
const react_1 = __importDefault(require("react"));
const utils_1 = require("../../utils");
const class_variance_authority_1 = require("class-variance-authority");
const loadingVariants = (0, class_variance_authority_1.cva)('animate-spin inline-block border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]', {
    variants: {
        size: {
            sm: 'h-4 w-4 border-2',
            default: 'h-6 w-6 border-2',
            lg: 'h-8 w-8 border-2',
            xl: 'h-12 w-12 border-4'
        },
        variant: {
            default: 'text-primary-600 dark:text-primary-500',
            secondary: 'text-neutral-600 dark:text-neutral-400',
            white: 'text-white'
        }
    },
    defaultVariants: {
        size: 'default',
        variant: 'default'
    }
});
exports.LoadingSpinner = react_1.default.forwardRef(({ className, size, variant, ...props }, ref) => ((0, jsx_runtime_1.jsx)("div", { ref: ref, className: (0, utils_1.cn)(loadingVariants({ size, variant }), 'rounded-full', className), role: "status", "aria-label": "Loading", ...props })));
exports.LoadingSpinner.displayName = 'LoadingSpinner';
// Loading Dots Component
const loadingDotsVariants = (0, class_variance_authority_1.cva)('inline-flex space-x-1', {
    variants: {
        size: {
            sm: '[&>div]:h-1 [&>div]:w-1',
            default: '[&>div]:h-2 [&>div]:w-2',
            lg: '[&>div]:h-3 [&>div]:w-3'
        },
        variant: {
            default: '[&>div]:bg-primary-600 dark:[&>div]:bg-primary-500',
            secondary: '[&>div]:bg-neutral-600 dark:[&>div]:bg-neutral-400',
            white: '[&>div]:bg-white'
        }
    },
    defaultVariants: {
        size: 'default',
        variant: 'default'
    }
});
exports.LoadingDots = react_1.default.forwardRef(({ className, size, variant, ...props }, ref) => ((0, jsx_runtime_1.jsxs)("div", { ref: ref, className: (0, utils_1.cn)(loadingDotsVariants({ size, variant }), className), role: "status", "aria-label": "Loading", ...props, children: [(0, jsx_runtime_1.jsx)("div", { className: "rounded-full animate-bounce [animation-delay:-0.3s]" }), (0, jsx_runtime_1.jsx)("div", { className: "rounded-full animate-bounce [animation-delay:-0.15s]" }), (0, jsx_runtime_1.jsx)("div", { className: "rounded-full animate-bounce" })] })));
exports.LoadingDots.displayName = 'LoadingDots';
exports.LoadingOverlay = react_1.default.forwardRef(({ className, isVisible = true, backdrop = 'light', children, ...props }, ref) => {
    if (!isVisible)
        return null;
    const backdropClasses = {
        light: 'bg-white/80',
        dark: 'bg-neutral-900/80',
        blur: 'bg-white/80 backdrop-blur-sm dark:bg-neutral-900/80'
    };
    return ((0, jsx_runtime_1.jsx)("div", { ref: ref, className: (0, utils_1.cn)('fixed inset-0 z-50 flex items-center justify-center', backdropClasses[backdrop], className), role: "status", "aria-label": "Loading", ...props, children: children || (0, jsx_runtime_1.jsx)(exports.LoadingSpinner, { size: "xl" }) }));
});
exports.LoadingOverlay.displayName = 'LoadingOverlay';
exports.LoadingButton = react_1.default.forwardRef(({ className, isLoading = false, loading, loadingText, children, disabled, isEditMode = false, customText, t, ...props }, ref) => {
    // 支援新舊兩種 API
    const isLoadingState = loading ?? isLoading;
    // 預設文字
    const defaultTexts = {
        add: t ? t('common.actions.add') : 'Add',
        update: t ? t('common.actions.update') : 'Update',
        adding: t ? t('common.status.adding') : 'Adding...',
        updating: t ? t('common.status.updating') : 'Updating...',
    };
    // 如果有 children，使用它們；否則使用 customText 或預設文字
    let buttonText = children;
    if (!buttonText) {
        if (isLoadingState) {
            buttonText = loadingText ||
                (customText?.updateLoading && isEditMode ? customText.updateLoading :
                    customText?.createLoading && !isEditMode ? customText.createLoading :
                        isEditMode ? defaultTexts.updating : defaultTexts.adding);
        }
        else {
            buttonText = (customText?.update && isEditMode ? customText.update :
                customText?.create && !isEditMode ? customText.create :
                    isEditMode ? defaultTexts.update : defaultTexts.add);
        }
    }
    return ((0, jsx_runtime_1.jsxs)("button", { ref: ref, className: (0, utils_1.cn)('inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none', className), disabled: disabled || isLoadingState, ...props, children: [isLoadingState && (0, jsx_runtime_1.jsx)(exports.LoadingSpinner, { size: "sm", variant: "white" }), buttonText] }));
});
exports.LoadingButton.displayName = 'LoadingButton';
// Simple Loading Component (alias for LoadingSpinner)
exports.Loading = exports.LoadingSpinner;
